import { useEffect, useMemo, useState } from "react";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { InlineWarning, ListingLinkButton, Panel } from "@/components/common/Primitives";
import { dateOnly, eur, km, num, pct } from "@/lib/format";

const TONE = {
  HIGH: "border-red-300 bg-red-50 text-red-800",
  MEDIUM: "border-amber-300 bg-amber-50 text-amber-800",
  INFO: "border-blue-300 bg-blue-50 text-blue-800",
};

export const RiskBadge = ({ severity, children, testid }) => (
  <span className={`chip ${TONE[severity] || TONE.INFO}`} data-testid={testid}>
    {children}
  </span>
);

export const RiskTable = () => {
  const [data, setData] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [severity, setSeverity] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [showChanges, setShowChanges] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([api.get("/risks"), api.get("/risks/changes")])
      .then(([risks, changes]) => {
        if (!alive) return;
        setData(risks.data);
        setSnapshot(changes.data.snapshot);
      })
      .catch((e) => alive && setError(apiError(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const items = useMemo(() => {
    let out = data?.items || [];
    if (severity) out = out.filter((i) => i.severity === severity);
    if (code) out = out.filter((i) => i.code === code);
    return out;
  }, [data, severity, code]);

  const usedCategories = useMemo(
    () => (data?.catalog || []).filter((c) => c.vehicles > 0),
    [data],
  );

  if (loading) {
    return (
      <Panel title="Risico's" testid="risk-panel">
        <div className="px-4 py-8 text-center text-[12.5px] text-slate-500">Risico's laden…</div>
      </Panel>
    );
  }
  if (error) {
    return (
      <Panel title="Risico's" testid="risk-panel">
        <div className="p-3">
          <InlineWarning tone="danger">{error}</InlineWarning>
        </div>
      </Panel>
    );
  }

  const s = data.summary;

  return (
    <div className="space-y-2" data-testid="risk-view">
      <Panel
        title="Risicoscan van de advertentieteksten"
        subtitle="Alleen woordelijke bewijzen uit de advertentie of providervelden — nooit geïnterpreteerd"
        testid="risk-summary-panel"
        actions={
          snapshot && (
            <button
              type="button"
              onClick={() => setShowChanges((v) => !v)}
              data-testid="risk-changes-toggle"
              className="rounded-sm border border-slate-300 bg-white px-2.5 py-1 text-[11.5px] font-semibold text-slate-700 hover:bg-slate-100"
            >
              {showChanges ? "Wijzigingen verbergen" : "Laatste herberekening vergelijken"}
            </button>
          )
        }
      >
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
          <RiskBadge severity="HIGH" testid="risk-count-high">
            {num(s.high)} hoog risico
          </RiskBadge>
          <RiskBadge severity="MEDIUM" testid="risk-count-medium">
            {num(s.medium)} middel
          </RiskBadge>
          <RiskBadge severity="INFO" testid="risk-count-info">
            {num(s.info)} informatief
          </RiskBadge>
          <span className="chip border-emerald-300 bg-emerald-50 text-emerald-800">
            {num(s.vehicles_clean)} auto's zonder risicosignaal
          </span>
          <span className="text-[11.5px] text-slate-600">
            {num(s.vehicles_with_high)} van {num(s.vehicles)} auto's heeft minimaal één hoog risico ·
            tekst geanalyseerd voor {num(s.text_analysed)} advertenties
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 px-3 py-2">
          {[
            ["", "Alle ernstniveaus"],
            ["HIGH", "Alleen hoog"],
            ["MEDIUM", "Alleen middel"],
            ["INFO", "Alleen informatief"],
          ].map(([key, label]) => (
            <button
              key={key || "all"}
              type="button"
              onClick={() => setSeverity(key)}
              data-testid={`risk-severity-${key || "all"}`}
              className={`rounded-sm border px-2 py-1 text-[11px] font-semibold transition-colors duration-150 ${
                severity === key
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
              }`}
            >
              {label}
            </button>
          ))}
          <select
            value={code}
            onChange={(e) => setCode(e.target.value)}
            data-testid="risk-code-select"
            className="h-7 rounded-sm border border-slate-300 bg-white px-2 text-[11.5px]"
          >
            <option value="">Alle risicosoorten</option>
            {usedCategories.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label} ({c.vehicles})
              </option>
            ))}
          </select>
          {(severity || code) && (
            <button
              type="button"
              onClick={() => {
                setSeverity("");
                setCode("");
              }}
              className="px-2 py-1 text-[11px] font-semibold text-blue-700 hover:underline"
            >
              Filters wissen
            </button>
          )}
        </div>

        {showChanges && snapshot && (
          <div className="border-b border-slate-200 bg-blue-50/40 px-3 py-2" data-testid="risk-changes">
            <div className="text-[12px] font-semibold text-slate-800">
              Laatste herberekening: {num(snapshot.vehicles_changed)} auto's gewijzigd ·{" "}
              {num(snapshot.flags_removed)} risico's vervallen · {num(snapshot.flags_added)} nieuw ·
              gemiddelde scorewijziging {snapshot.average_score_delta > 0 ? "+" : ""}
              {num(snapshot.average_score_delta, { decimals: 1 })}
            </div>
            <div className="mt-1 max-h-[220px] overflow-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Voertuig</th>
                    <th className="text-right">Score oud → nieuw</th>
                    <th>Vervallen</th>
                    <th>Nieuw</th>
                    <th>Reden</th>
                  </tr>
                </thead>
                <tbody>
                  {(snapshot.changes || []).slice(0, 40).map((c) => (
                    <tr key={c.listing_id} data-testid={`risk-change-${c.listing_id}`}>
                      <td className="text-[11.5px]">{c.label}</td>
                      <td className="num text-[11.5px]">
                        {num(c.old_deal_score)} → <b>{num(c.new_deal_score)}</b>
                      {c.score_delta ? (
                          <span
                            className={
                              c.score_delta > 0 ? "ml-1 text-emerald-700" : "ml-1 text-red-700"
                            }
                          >
                            ({c.score_delta > 0 ? "+" : ""}
                            {num(c.score_delta, { decimals: 1 })})
                          </span>
                        ) : null}
                      </td>
                      <td className="text-[10.5px] text-emerald-700">
                        {(c.removed_codes || []).join(", ") || "—"}
                      </td>
                      <td className="text-[10.5px] text-red-700">
                        {(c.added_codes || []).join(", ") || "—"}
                      </td>
                      <td className="text-[10.5px] text-slate-600">{c.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Panel>

      <Panel
        title={`${num(items.length)} risicoregels`}
        subtitle="Eén regel per gedetecteerd risico, met de letterlijke zin uit de advertentie"
        testid="risk-table-panel"
      >
        {items.length === 0 ? (
          <div className="flex items-center justify-center gap-2 px-4 py-10 text-[12.5px] text-emerald-700">
            <ShieldCheck className="h-4 w-4" />
            Geen risico's gevonden binnen deze filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="risk-table">
              <thead>
                <tr>
                  <th>Ernst</th>
                  <th>Risico</th>
                  <th>Voertuig</th>
                  <th>1e toelating</th>
                  <th className="text-right">KM</th>
                  <th className="text-right">Vraagprijs</th>
                  <th className="text-right">Bijdrage</th>
                  <th className="text-right">Score</th>
                  <th className="text-right">Aftrek</th>
                  <th>Bewijs uit advertentie</th>
                  <th>Advertentie</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i, index) => (
                  <tr key={`${i.listing_id}-${i.code}-${index}`} data-testid={`risk-row-${i.listing_id}-${i.code}`}>
                    <td>
                      <RiskBadge severity={i.severity}>
                        <ShieldAlert className="mr-1 h-3 w-3" />
                        {i.severity_label}
                      </RiskBadge>
                    </td>
                    <td className="text-[11.5px] font-semibold text-slate-800">
                      {i.label}
                      <div className="text-[10px] font-normal text-slate-500">
                        {(i.matched_terms || []).slice(0, 3).join(" · ")}
                        {i.source === "PROVIDERVELD" ? " · providerveld" : ""}
                      </div>
                    </td>
                    <td className="max-w-[210px] truncate text-[11.5px]">
                      {[i.make, i.model, i.variant].filter(Boolean).join(" ")}
                    </td>
                    <td className="num text-[11.5px]">{dateOnly(i.first_registration)}</td>
                    <td className="num text-[11.5px]">{km(i.mileage_km)}</td>
                    <td className="num text-[11.5px]">{eur(i.price)}</td>
                    <td className="num text-[11.5px]">
                      {i.contribution == null ? "—" : eur(i.contribution)}
                    </td>
                    <td className="num text-[11.5px]">
                      {i.deal_score == null ? "—" : num(i.deal_score)}
                    </td>
                    <td className="num text-[11.5px]">
                      {i.duplicate_of_group ? (
                        <span className="text-slate-400">al verrekend</span>
                      ) : i.penalty ? (
                        <span className="font-semibold text-red-700">
                          −{num(i.penalty, { decimals: 1 })}
                        </span>
                      ) : (
                        <span className="text-slate-400">geen</span>
                      )}
                    </td>
                    <td className="max-w-[380px] whitespace-normal break-words text-[10.5px] italic leading-tight text-slate-600">
                      “{i.evidence}”
                    </td>
                    <td>
                      <ListingLinkButton url={i.url} testid={`risk-url-${i.listing_id}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="px-3 py-2 text-[10.5px] leading-snug text-slate-500">
          Eén onderliggend risico levert één score-aftrek op, ook als de advertentie meerdere
          synoniemen gebruikt. Zinnen die het tegendeel stellen ("Unfallfrei", "TÜV neu",
          "Scheckheftgepflegt") zetten géén vlag. Aftrek per risicosoort is instelbaar bij
          Instellingen → Risico's.
        </p>
      </Panel>
    </div>
  );
};

export default RiskTable;
